import { Search, Sparkles, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  CANVAS_PROMPT_TEMPLATES,
  CANVAS_TEMPLATE_CATEGORIES,
  type CanvasPromptTemplate,
} from './canvas-templates'

interface Props {
  open: boolean
  onClose: () => void
  onApplyTemplate: (template: CanvasPromptTemplate) => void
}

export function CanvasTemplateSidebar({ open, onClose, onApplyTemplate }: Props) {
  const [category, setCategory] = useState<string>('All')
  const [query, setQuery] = useState('')

  const filteredTemplates = useMemo(() => {
    const q = query.trim().toLowerCase()
    return CANVAS_PROMPT_TEMPLATES.filter((template) => {
      const categoryMatch = category === 'All' || template.category === category
      if (!categoryMatch) return false
      if (!q) return true
      const haystack = [
        template.name,
        template.category,
        template.description,
        ...template.tags,
        ...Object.values(template.fields),
      ].join(' ').toLowerCase()
      return haystack.includes(q)
    })
  }, [category, query])

  if (!open) return null

  return (
    <aside
      style={{
        position: 'absolute',
        top: 16,
        left: 16,
        bottom: 16,
        width: 340,
        background: 'rgba(15,23,42,0.96)',
        border: '1px solid #334155',
        borderRadius: 12,
        color: '#e2e8f0',
        fontFamily: 'Inter, system-ui, sans-serif',
        boxShadow: '0 20px 70px rgba(0,0,0,0.5)',
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: 14,
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'linear-gradient(135deg,#6366f1,#8b5cf6,#06b6d4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Sparkles size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>模板库</div>
          <div style={{ color: '#64748b', fontSize: 11 }}>点击模板创建预填卡片</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="关闭模板库"
          style={{
            background: 'transparent',
            border: '1px solid #334155',
            color: '#94a3b8',
            width: 28,
            height: 28,
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div style={{ padding: 12, borderBottom: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#020617',
            border: '1px solid #334155',
            borderRadius: 8,
            padding: '7px 9px',
          }}
        >
          <Search size={14} color="#64748b" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索风格、用途、主体"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#e2e8f0',
              fontSize: 12,
            }}
          />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {['All', ...CANVAS_TEMPLATE_CATEGORIES].map((cat) => {
            const active = category === cat
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setCategory(cat)}
                style={{
                  background: active ? 'rgba(139,92,246,0.22)' : 'transparent',
                  border: `1px solid ${active ? '#8b5cf6' : '#334155'}`,
                  color: active ? '#e9d5ff' : '#94a3b8',
                  borderRadius: 999,
                  padding: '4px 8px',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                {cat}
              </button>
            )
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filteredTemplates.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 12, padding: 16, textAlign: 'center' }}>没有匹配的模板</div>
        ) : (
          filteredTemplates.map((template) => (
            <TemplateCard key={template.id} template={template} onApply={() => onApplyTemplate(template)} />
          ))
        )}
      </div>
    </aside>
  )
}

function TemplateCard({ template, onApply }: { template: CanvasPromptTemplate; onApply: () => void }) {
  return (
    <button
      type="button"
      onClick={onApply}
      style={{
        textAlign: 'left',
        background: '#0b1120',
        border: '1px solid #1e293b',
        borderRadius: 10,
        padding: 12,
        cursor: 'pointer',
        color: '#e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#8b5cf6'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#1e293b'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#a78bfa', fontWeight: 700 }}>{template.category}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#f8fafc' }}>{template.name}</span>
      </div>
      <div style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>{template.description}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {template.tags.map((tag) => (
          <span
            key={tag}
            style={{
              color: '#67e8f9',
              background: 'rgba(6,182,212,0.12)',
              border: '1px solid rgba(6,182,212,0.22)',
              borderRadius: 999,
              padding: '2px 6px',
              fontSize: 10,
            }}
          >
            {tag}
          </span>
        ))}
      </div>
      <div
        style={{
          color: '#64748b',
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={template.fields.subject}
      >
        {template.fields.subject}
      </div>
    </button>
  )
}
